import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Typography,
} from '@material-ui/core';
import PaginationControls from '@/se/components/collection/PaginationControls';
import { Alert } from '@material-ui/lab';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Sync as SyncIcon,
  Schedule as ScheduleIcon,
  Visibility as PreviewIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Error as ErrorIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import TitleAction from '@/se/components/TitleAction';
import { DefaultTitle } from '@/se/components/entity/EntityRouter';

import {
  analyticsSchedulesQuery,
  analyticsExecutionsQuery,
  analyticsScheduleRecipientsQuery,
  triggerAnalyticsReportMutation,
  retryFailedExecutionMutation,
  syncAnalyticsSchedulesMutation,
  previewAnalyticsReportMutation,
} from '../../../graph/analytics-schedules';

import type { AnalyticsScheduleView, AnalyticsExecution, ReportType, ExecutionStatus, PreviewResult } from './types';
import { TIME_RANGE_OPTIONS, EXECUTION_PAGE_SIZE, EXECUTION_POLL_INTERVAL, normalizeStatus } from './utils';
import { SchedulesTable, ExecutionsTable, PreviewModal, RecipientsDialog } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
    overflow: 'hidden',
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterControl: {
    minWidth: 140,
  },
  tabs: {
    marginBottom: theme.spacing(2),
  },
  lambdaDisabledBanner: {
    marginBottom: theme.spacing(2),
  },
  searchField: {
    marginBottom: theme.spacing(2),
    maxWidth: 300,
  },
}));

const ScheduledTasksMonitor: React.FC = () => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Search filter for schedules tab
  const [scheduleSearch, setScheduleSearch] = useState('');

  // Filters for executions tab
  const [orgFilter, setOrgFilter] = useState<number | ''>('');
  const [reportTypeFilter, setReportTypeFilter] = useState<ReportType | ''>('');
  const [statusFilter, setStatusFilter] = useState<ExecutionStatus | ''>('');
  const [daysBackFilter, setDaysBackFilter] = useState<number>(30);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);

  // Preview modal state
  const [previewModal, setPreviewModal] = useState<{
    open: boolean;
    orgId: number;
    orgName: string;
    reportType: ReportType;
  } | null>(null);
  const [previewData, setPreviewData] = useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Recipients dialog state
  const [recipientsDialog, setRecipientsDialog] = useState<{
    open: boolean;
    orgId: number;
    orgName: string;
    reportType: ReportType;
  } | null>(null);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  // Confirmation dialog state for trigger actions
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    orgId: number;
    orgName: string;
    reportType: ReportType;
    recipientCount: number;
  } | null>(null);
  const [triggerLoading, setTriggerLoading] = useState(false);

  // Queries
  const {
    data: schedulesData,
    loading: schedulesLoading,
    refetch: refetchSchedules,
  } = useQuery(analyticsSchedulesQuery, {
    fetchPolicy: 'network-only',
  });

  const {
    data: executionsData,
    loading: executionsLoading,
    refetch: refetchExecutions,
  } = useQuery(analyticsExecutionsQuery, {
    variables: {
      organizationId: orgFilter || undefined,
      reportType: reportTypeFilter || undefined,
      status: statusFilter || undefined,
      daysBack: daysBackFilter,
      limit: EXECUTION_PAGE_SIZE,
      offset: currentPage * EXECUTION_PAGE_SIZE,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Reset page when filters change
  const handleFilterChange = useCallback(
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
      (value: T) => {
        setter(value);
        setCurrentPage(0);
      },
    []
  );

  // Mutations
  const [triggerReport] = useMutation(triggerAnalyticsReportMutation);
  const [retryExecution] = useMutation(retryFailedExecutionMutation);
  const [syncSchedules, { loading: syncing }] = useMutation(syncAnalyticsSchedulesMutation);
  const [previewReport] = useMutation(previewAnalyticsReportMutation);
  const [fetchRecipients] = useLazyQuery(analyticsScheduleRecipientsQuery, {
    fetchPolicy: 'network-only',
  });

  // Derived data
  const lambdaEnabled: boolean = schedulesData?.analyticsLambdaEnabled ?? true;
  const schedules: AnalyticsScheduleView[] = schedulesData?.analyticsSchedules || [];
  const filteredSchedules = useMemo(() => {
    if (!scheduleSearch.trim()) return schedules;
    const search = scheduleSearch.toLowerCase().trim();
    return schedules.filter(
      s =>
        s.orgName.toLowerCase().includes(search) ||
        s.orgSlug?.toLowerCase().includes(search) ||
        s.orgId.toString().includes(search)
    );
  }, [schedules, scheduleSearch]);

  const executions: AnalyticsExecution[] = executionsData?.analyticsExecutions?.items || [];
  const executionsTotalCount: number = executionsData?.analyticsExecutions?.totalCount || 0;
  const totalPages = useMemo(() => Math.ceil(executionsTotalCount / EXECUTION_PAGE_SIZE), [executionsTotalCount]);

  // Check if there are any running executions
  const hasRunningExecutions = useMemo(
    () => executions.some(e => normalizeStatus(e.status) === 'running'),
    [executions]
  );

  // Auto-polling when there are running executions
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (hasRunningExecutions && tabValue === 1) {
      pollIntervalRef.current = setInterval(() => {
        refetchExecutions();
      }, EXECUTION_POLL_INTERVAL);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [hasRunningExecutions, tabValue, refetchExecutions]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    refetchSchedules();
    refetchExecutions();
  }, [refetchSchedules, refetchExecutions]);

  const handleSync = useCallback(async () => {
    try {
      const result = await syncSchedules();
      const data = result.data?.syncAnalyticsSchedules;
      if (data?.success) {
        setSnackbar({ open: true, message: data.message || 'Sync complete', severity: 'success' });
        refetchSchedules();
      } else {
        setSnackbar({ open: true, message: data?.message || 'Sync failed', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Sync error: ${(e as Error).message}`, severity: 'error' });
    }
  }, [syncSchedules, refetchSchedules]);

  const handleTriggerClick = useCallback(
    (orgId: number, orgName: string, reportType: ReportType, recipientCount: number) => {
      setConfirmDialog({ open: true, orgId, orgName, reportType, recipientCount });
    },
    []
  );

  const handleConfirmTrigger = useCallback(async () => {
    if (!confirmDialog) return;
    const { orgId, reportType } = confirmDialog;

    setTriggerLoading(true);
    try {
      const result = await triggerReport({ variables: { organizationId: orgId, reportType } });
      const data = result.data?.triggerAnalyticsReport;
      if (data?.success) {
        setSnackbar({
          open: true,
          message: 'Report triggered - status will auto-update when complete',
          severity: 'success',
        });
        setTabValue(1);
        setTimeout(() => refetchExecutions(), 500);
      } else {
        setSnackbar({ open: true, message: data?.message || 'Trigger failed', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Trigger error: ${(e as Error).message}`, severity: 'error' });
    } finally {
      setTriggerLoading(false);
      setConfirmDialog(null);
    }
  }, [confirmDialog, triggerReport, refetchExecutions]);

  const handleRetry = useCallback(
    async (executionId: number) => {
      try {
        const result = await retryExecution({ variables: { executionId } });
        const data = result.data?.retryFailedExecution;
        if (data?.success) {
          setSnackbar({ open: true, message: data.message || 'Retry triggered', severity: 'success' });
          refetchExecutions();
        } else {
          setSnackbar({ open: true, message: data?.message || 'Retry failed', severity: 'error' });
        }
      } catch (e) {
        setSnackbar({ open: true, message: `Retry error: ${(e as Error).message}`, severity: 'error' });
      }
    },
    [retryExecution, refetchExecutions]
  );

  const handlePreview = useCallback(
    async (orgId: number, orgName: string, reportType: ReportType) => {
      setPreviewModal({ open: true, orgId, orgName, reportType });
      setPreviewData(null);
      setPreviewLoading(true);

      try {
        const result = await previewReport({ variables: { organizationId: orgId, reportType } });
        setPreviewData(result.data?.previewAnalyticsReport || null);
      } catch (e) {
        setPreviewData({
          success: false,
          message: `Preview error: ${(e as Error).message}`,
        });
      } finally {
        setPreviewLoading(false);
      }
    },
    [previewReport]
  );

  const handleClosePreview = useCallback(() => {
    setPreviewModal(null);
    setPreviewData(null);
  }, []);

  const handleViewRecipients = useCallback(
    async (orgId: number, orgName: string, reportType: ReportType) => {
      setRecipientsDialog({ open: true, orgId, orgName, reportType });
      setRecipients([]);
      setRecipientsLoading(true);

      try {
        const result = await fetchRecipients({
          variables: { organizationId: orgId, reportType },
        });
        setRecipients(result.data?.analyticsScheduleRecipients || []);
      } catch (e) {
        setRecipients([]);
      } finally {
        setRecipientsLoading(false);
      }
    },
    [fetchRecipients]
  );

  const handleCloseRecipients = useCallback(() => {
    setRecipientsDialog(null);
    setRecipients([]);
  }, []);

  return (
    <Box className={classes.root}>
      <TitleAction
        Title={() => (
          <DefaultTitle>
            <ScheduleIcon style={{ marginRight: 8 }} />
            Scheduled Tasks Monitor
          </DefaultTitle>
        )}
      >
        <Box className={classes.headerActions}>
          <Button
            variant="outlined"
            size="small"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            Sync
          </Button>
          <IconButton onClick={handleRefresh} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
      </TitleAction>

      {!lambdaEnabled && (
        <Alert severity="info" icon={<InfoIcon />} className={classes.lambdaDisabledBanner}>
          Lambda analytics is disabled. Showing schedule configuration only. Preview and trigger features require Lambda
          to be enabled.
        </Alert>
      )}

      <Tabs
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Schedules" />
        {lambdaEnabled && (
          <Tab label={`Execution History${executionsTotalCount > 0 ? ` (${executionsTotalCount})` : ''}`} />
        )}
      </Tabs>

      {/* Schedules Tab */}
      <Box className={classes.tabContent} style={{ display: tabValue === 0 ? 'flex' : 'none' }}>
        <TextField
          className={classes.searchField}
          size="small"
          variant="outlined"
          placeholder="Search by name, slug, or ID..."
          value={scheduleSearch}
          onChange={e => setScheduleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <SchedulesTable
          schedules={filteredSchedules}
          loading={schedulesLoading}
          lambdaEnabled={lambdaEnabled}
          searchQuery={scheduleSearch}
          onPreview={handlePreview}
          onTrigger={handleTriggerClick}
          onViewRecipients={handleViewRecipients}
        />
      </Box>

      {/* Executions Tab */}
      <Box className={classes.tabContent} style={{ display: lambdaEnabled && tabValue === 1 ? 'flex' : 'none' }}>
        <Box className={classes.filters}>
          {hasRunningExecutions && (
            <Chip
              icon={<CircularProgress size={14} />}
              label="Auto-refreshing..."
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={orgFilter}
              onChange={e => handleFilterChange(setOrgFilter)(e.target.value as number | '')}
              label="Organization"
            >
              <MenuItem value="">All</MenuItem>
              {schedules.map(s => (
                <MenuItem key={s.orgId} value={s.orgId}>
                  {s.orgName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportTypeFilter}
              onChange={e => handleFilterChange(setReportTypeFilter)(e.target.value as ReportType | '')}
              label="Report Type"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="daily_csv">Daily CSV</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={e => handleFilterChange(setStatusFilter)(e.target.value as ExecutionStatus | '')}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="running">Running</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={daysBackFilter}
              onChange={e => handleFilterChange(setDaysBackFilter)(e.target.value as number)}
              label="Time Range"
            >
              {TIME_RANGE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <ExecutionsTable
          executions={executions}
          schedules={schedules}
          loading={executionsLoading}
          daysBack={daysBackFilter}
          onRetry={handleRetry}
        />

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={executionsTotalCount}
          pageSize={EXECUTION_PAGE_SIZE}
          onPageChange={setCurrentPage}
          loading={executionsLoading}
        />
      </Box>

      {/* Preview Modal */}
      <PreviewModal
        open={previewModal?.open ?? false}
        onClose={handleClosePreview}
        preview={previewData}
        loading={previewLoading}
        orgName={previewModal?.orgName ?? ''}
        reportType={previewModal?.reportType ?? 'weekly'}
      />

      {/* Recipients Dialog */}
      <RecipientsDialog
        open={recipientsDialog?.open ?? false}
        onClose={handleCloseRecipients}
        orgName={recipientsDialog?.orgName ?? ''}
        reportType={recipientsDialog?.reportType ?? 'weekly'}
        recipients={recipients}
        loading={recipientsLoading}
      />

      {/* Confirmation Dialog for Trigger Actions */}
      <ConfirmTriggerDialog
        dialog={confirmDialog}
        loading={triggerLoading}
        onConfirm={handleConfirmTrigger}
        onCancel={() => setConfirmDialog(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface ConfirmTriggerDialogProps {
  dialog: {
    open: boolean;
    orgId: number;
    orgName: string;
    reportType: ReportType;
    recipientCount: number;
  } | null;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmTriggerDialog: React.FC<ConfirmTriggerDialogProps> = ({ dialog, loading, onConfirm, onCancel }) => {
  if (!dialog) return null;

  const isCsv = dialog.reportType.toLowerCase() === 'daily_csv';

  return (
    <Dialog open={dialog.open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <ErrorIcon color="error" />
          <Typography variant="h6">Confirm Report Trigger</Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          <strong>
            {isCsv ? 'This action will upload a CSV to S3!' : 'This action will send real emails to customers!'}
          </strong>
        </Alert>
        <Typography variant="body1" gutterBottom>
          You are about to trigger a <strong>{dialog.reportType}</strong> report for:
        </Typography>
        <Typography variant="h6" style={{ margin: '8px 0' }}>
          {dialog.orgName}
        </Typography>
        {!isCsv && dialog.recipientCount > 0 && (
          <Typography variant="body2" color="textSecondary">
            This will send emails to <strong>{dialog.recipientCount}</strong> recipient
            {dialog.recipientCount !== 1 ? 's' : ''}.
          </Typography>
        )}
        <Alert severity="info" style={{ marginTop: 16 }} icon={<PreviewIcon fontSize="small" />}>
          Tip: Use the Preview button (eye icon) to verify the report content before triggering.
        </Alert>
      </DialogContent>
      <DialogActions style={{ padding: 16 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="secondary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <PlayIcon />}
        >
          {loading ? 'Triggering...' : 'Yes, Trigger Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduledTasksMonitor;
